import type { PhoneScript } from "@/types/campaigns";
import { isDefaultOrgOutcomeSlug } from "@/constants/orgOutcomes";
import { ORG_OUTCOMES_FEATURE_FLAG } from "@/hooks/useOrgOutcomesEnabled";
import { getBrowserPostHog } from "@/lib/posthog-browser";
import { listOrgOutcomes } from "@/services/orgOutcomes";
import { filterApplicableOutcomesForSave } from "@/utils/orgOutcomes/filterApplicableOutcomesForSave";
import { customOrgOutcomesFromCatalog } from "@/utils/orgOutcomes/scriptOutcomeOptions";
import { uniqueApplicableOutcomeKeys } from "@/utils/orgOutcomes/uniqueApplicableOutcomeKeys";

function isOrgOutcomesUiEnabled(): boolean {
  try {
    const client = getBrowserPostHog();
    if (!client || typeof client.getFeatureFlagResult !== "function") {
      // No PostHog client (tests / early boot): keep sanitizing so saves stay safe.
      return true;
    }
    // Call on the client so `this` stays bound; PostHog may also throw if
    // flag state is not ready yet.
    const result = client.getFeatureFlagResult(ORG_OUTCOMES_FEATURE_FLAG) as
      | { enabled?: boolean }
      | undefined;
    return result?.enabled === true;
  } catch {
    return true;
  }
}

/**
 * Save-boundary sanitizer for `applicableOutcomes`.
 * Drops system keys always; when the org catalog loads, also drops keys
 * missing from customs. On catalog fetch failure, keeps non-system keys
 * (does not wipe associations).
 *
 * Empty result after sanitize is `[]` (clear on Galactus), not `undefined`
 * (leave unchanged). `undefined` is reserved for scripts that never set the field.
 * Skipped when `ui-org-outcomes` is off so unrelated saves do not rewrite the field.
 */
export async function sanitizeApplicableOutcomesOnScript<
  T extends Pick<PhoneScript, "applicableOutcomes">,
>(script: T): Promise<T> {
  if (!isOrgOutcomesUiEnabled()) {
    return script;
  }

  if (script.applicableOutcomes == null) {
    return script;
  }

  if (script.applicableOutcomes.length === 0) {
    return { ...script, applicableOutcomes: [] };
  }

  const withoutSystem = uniqueApplicableOutcomeKeys(
    script.applicableOutcomes.filter((key) => !isDefaultOrgOutcomeSlug(key))
  );

  const result = await listOrgOutcomes();
  if (!result.success || !result.data) {
    return {
      ...script,
      applicableOutcomes: withoutSystem,
    };
  }

  const catalogKeys = customOrgOutcomesFromCatalog(result.data).map(
    (o) => o.key
  );
  const cleaned = filterApplicableOutcomesForSave(
    withoutSystem,
    catalogKeys
  );

  return {
    ...script,
    applicableOutcomes: cleaned,
  };
}
